import { useState, type ReactElement } from 'react';
import { LayoutTemplate, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/shell/PageHeader';

import { useCreateTenantTemplate, useTenantTemplates } from '@/features/platform/api';
import { CreateTemplateDialog } from '@/features/platform/components/CreateTemplateDialog';

export function PlatformTemplatesPage(): ReactElement {
  const { t } = useTranslation();
  const templatesQuery = useTenantTemplates();
  const createTemplate = useCreateTenantTemplate();
  const [createOpen, setCreateOpen] = useState(false);

  const templates = templatesQuery.data ?? [];

  return (
    <>
      <PageHeader
        icon={<LayoutTemplate className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.platform.templates.heading')}
        subtitle={t('routes.platform.templates.subheading')}
        actions={
          <Button type="button" size="sm" onClick={() => { setCreateOpen(true); }}>
            <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
            {t('routes.platform.templates.createAction')}
          </Button>
        }
      />
      <div className="space-y-4 bg-background px-8 py-5">
        {templatesQuery.isPending && (
          <div className="space-y-2" aria-hidden="true">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-md" />
            ))}
          </div>
        )}

        {templatesQuery.isError && (
          <div className="max-w-xl">
            <FetchErrorCallout
              error={templatesQuery.error}
              heading={t('routes.platform.templates.error.heading')}
              message={t('routes.platform.templates.error.message')}
              codeLabel={t('routes.platform.templates.error.codeLabel')}
              onRetry={() => {
                templatesQuery.refetch().catch(() => undefined);
              }}
              retryLabel={t('routes.platform.templates.error.retry')}
            />
          </div>
        )}

        {templatesQuery.isSuccess && templates.length === 0 && (
          <p className="text-sm text-muted-foreground" role="status">
            {t('routes.platform.templates.empty')}
          </p>
        )}

        {templatesQuery.isSuccess && templates.length > 0 && (
          <DataTableTable aria-label={t('routes.platform.templates.listAriaLabel')}>
            <DataTableThead>
              <DataTableHeaderRow>
                <DataTableTh widthClassName="w-[24%]">
                  {t('routes.platform.templates.table.name')}
                </DataTableTh>
                <DataTableTh widthClassName="w-[36%]">
                  {t('routes.platform.templates.table.description')}
                </DataTableTh>
                <DataTableTh widthClassName="w-[12%]">
                  {t('routes.platform.templates.table.default')}
                </DataTableTh>
                <DataTableTh widthClassName="w-[14%]">
                  {t('routes.platform.templates.table.createdBy')}
                </DataTableTh>
                <DataTableTh widthClassName="w-[14%]">
                  {t('routes.platform.templates.table.createdAt')}
                </DataTableTh>
              </DataTableHeaderRow>
            </DataTableThead>
            <DataTableTbody>
              {templates.map((tpl) => (
                <DataTableTr key={tpl.name} className="even:bg-muted/40">
                  <DataTableTd className="font-mono text-[12px] text-foreground">
                    {tpl.name}
                  </DataTableTd>
                  <DataTableTd dataOffset className="text-foreground/90">
                    {tpl.description ?? ''}
                  </DataTableTd>
                  <DataTableTd dataOffset>
                    {tpl.isDefault && (
                      <Badge variant="secondary">{t('routes.platform.templates.default')}</Badge>
                    )}
                  </DataTableTd>
                  <DataTableTd dataOffset className="text-muted-foreground">
                    {tpl.createdBy ?? ''}
                  </DataTableTd>
                  <DataTableTd dataOffset className="text-muted-foreground">
                    {tpl.createdAt ?? ''}
                  </DataTableTd>
                </DataTableTr>
              ))}
            </DataTableTbody>
          </DataTableTable>
        )}
      </div>

      <CreateTemplateDialog
        open={createOpen}
        onOpenChange={(next) => {
          setCreateOpen(next);
          if (!next) createTemplate.reset();
        }}
        onSubmit={(input) => {
          createTemplate.mutate(input, {
            onSuccess: () => {
              setCreateOpen(false);
              createTemplate.reset();
            },
          });
        }}
        pending={createTemplate.isPending}
        submitError={createTemplate.error}
      />
    </>
  );
}
